import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Card, Button, Table, Space, Modal, Input, Upload, message, Select, Tooltip, Image, Tree, Breadcrumb } from 'antd';
import { UploadOutlined, FolderAddOutlined, FolderOpenOutlined, FileOutlined } from '@ant-design/icons';
import api from '../api/http';
import dayjs from 'dayjs';
import SimplePdfViewer from '../components/SimplePdfViewer';
import heic2any from 'heic2any';
import AuthedImage from '../components/AuthedImage';
import { buildMinioProxyUrl, fetchBlob, downloadWithAuth, openWithAuth } from '../utils/authedResource';

const isDirRecord = (r) => (r?.mimeType === 'application/x-directory') || String(r?.objectName || '').endsWith('/');

const extractDirectSubFolderNames = (allRecords, currentPrefix) => {
  const basePrefix = currentPrefix ? (currentPrefix.endsWith('/') ? currentPrefix : `${currentPrefix}/`) : '';
  const set = new Set();
  (allRecords || []).forEach((r) => {
    const on = String(r?.objectName || '');
    if (basePrefix && !on.startsWith(basePrefix)) return;
    if (!isDirRecord(r)) return;
    const rest = basePrefix ? on.substring(basePrefix.length) : on;
    const clean = String(rest || '').replace(/^\/+|\/+$/g, '');
    if (!clean) return;
    const firstSeg = clean.split('/')[0];
    if (firstSeg) set.add(firstSeg);
  });
  return Array.from(set);
};

function FileManagerPage() {
  // 状态管理
  const [prefix, setPrefix] = useState('');              // 当前所在的文件夹路径（多级以 / 分隔）
  const [records, setRecords] = useState([]);            // 当前视图的文件（不含文件夹）
  const [rootFolders, setRootFolders] = useState([]);    // 根目录文件夹列表
  const [subFolders, setSubFolders] = useState([]);      // 当前文件夹的直接子文件夹
  const [treeData, setTreeData] = useState([{ title: '根目录', key: '', icon: <FolderOpenOutlined /> }]);
  const [loading, setLoading] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [newFolder, setNewFolder] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]); // 缓存刚选择的文件用于显示（立即上传后清空）
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastUploadError, setLastUploadError] = useState('');
  const [preview, setPreview] = useState({ open: false, url: '', mime: '', name: '', objectName: '', converted: false, revokeUrl: null });
  const [categories, setCategories] = useState([]);            // 已存在分类列表
  const [category, setCategory] = useState('');                // 当前选择的分类（可为空自动推断）
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetFolder, setTargetFolder] = useState('');  // 要重命名/删除的根目录文件夹名称
  const [renameValue, setRenameValue] = useState('');

  const loadRootFolders = useCallback(async () => {
    const rootRes = await api.get('/files?prefix=');
    if (!(rootRes.data && rootRes.data.success)) return [];
    const allRoot = (rootRes.data.records || []).filter(r => !!r.fileName);
    const rf = extractDirectSubFolderNames(allRoot, '');
    setRootFolders(rf);
    setTreeData([{ title: '根目录', key: '', icon: <FolderOpenOutlined />, children: rf.map(name => ({ title: name, key: name, icon: <FolderOpenOutlined /> })) }]);
    return rf;
  }, []);

  // 拉取列表：根据 prefix 决定展示内容
  const fetchList = async (refreshRootFolders = false) => {
    setLoading(true);
    try {
      const res = await api.get(`/files?prefix=${encodeURIComponent(prefix)}`);
      if (!(res.data && res.data.success)) {
        message.error(res.data?.error || '获取文件列表失败');
        return;
      }
      const all = (res.data.records || []).filter(r => !!r.fileName); // 去除 fileName 为空的

      const basePrefix = prefix ? (prefix.endsWith('/') ? prefix : `${prefix}/`) : '';
      const files = all.filter(r => {
        const on = r.objectName || '';
        if (basePrefix && !on.startsWith(basePrefix)) return false;
        const rest = basePrefix ? on.substring(basePrefix.length) : on;
        if (isDirRecord(r)) return false;
        return !rest.includes('/');
      });
      setRecords(files);
      setSubFolders(extractDirectSubFolderNames(all, prefix));

      if (refreshRootFolders) {
        await loadRootFolders();
      }
    } catch (e) {
      console.error('fetch files error', e);
      message.error('获取文件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadTreeChildren = useCallback(async (parentPrefix) => {
    const res = await api.get(`/files?prefix=${encodeURIComponent(parentPrefix || '')}`);
    if (!(res.data && res.data.success)) return [];
    const all = (res.data.records || []).filter(r => !!r.fileName);
    const childNames = extractDirectSubFolderNames(all, parentPrefix || '');
    return childNames.map((name) => {
      const childPath = parentPrefix ? `${parentPrefix}/${name}` : name;
      return { title: name, key: childPath, icon: <FolderOpenOutlined /> };
    });
  }, []);

  const patchTreeChildren = useCallback((nodes, targetKey, children) => (
    (nodes || []).map((n) => {
      if (n.key === targetKey) return { ...n, children };
      if (!n.children) return n;
      return { ...n, children: patchTreeChildren(n.children, targetKey, children) };
    })
  ), []);

  const onLoadData = ({ key, children }) => new Promise((resolve) => {
    if (children && children.length) {
      resolve();
      return;
    }
    loadTreeChildren(key).then((childNodes) => {
      setTreeData((prev) => patchTreeChildren(prev, key, childNodes));
      resolve();
    }).catch(() => resolve());
  });

  useEffect(() => {
    fetchList();
  }, [prefix]);

  useEffect(() => {
    loadRootFolders();
  }, [loadRootFolders]);
  useEffect(() => { // 初始化加载分类列表
    const loadCats = async () => {
      try { const res = await api.get('/files/categories'); if (res.data?.success) setCategories(res.data.categories || []); } catch(e){}
    }; loadCats();
  }, []);

  // 新建文件夹（根或子）
  const openCreateFolder = () => { setNewFolder(''); setFolderModalOpen(true); };
  const createFolder = async () => {
    if (!newFolder.trim()) { message.error('请输入文件夹名称'); return; }
    try {
      const res = await api.post('/files/folders', { path: newFolder.trim(), prefix });
      if (res.data && res.data.success) {
        message.success('文件夹创建成功');
        setFolderModalOpen(false);
        const full = prefix ? `${prefix}/${newFolder.trim()}` : newFolder.trim();
        setPrefix(full); // 进入新文件夹
        fetchList(true);
      } else message.error(res.data?.error || '创建失败');
    } catch (e) { message.error('创建失败'); }
  };

  // 自动上传：选择文件后立即上传到当前 prefix
  // 上传触发队列，避免多次 beforeUpload 事件造成重复提交
  const [pendingUploadIds, setPendingUploadIds] = useState(new Set());
  const schedulingRef = useRef(false); // 防止同一批次多次触发
  const triggerUpload = async (files) => {
    if (!prefix) { message.error('请先进入一个文件夹'); return; }
    if (!files.length) return;
    // 去重：基于文件名+大小+lastModified 组合生成 key
    const unique = [];
    const seen = new Set();
    for (const f of files) {
      const key = [f.name, f.size, f.lastModified].join('::');
      if (!seen.has(key)) { seen.add(key); unique.push(f); }
    }
    // 检查是否已经在等待队列
    const newKeys = unique.map(f => [f.name, f.size, f.lastModified].join('::')).filter(k => !pendingUploadIds.has(k));
    if (!newKeys.length) { return; }
  setPendingUploadIds(prev => new Set([...Array.from(prev), ...newKeys]));
  schedulingRef.current = false; // 当前批次进入正式上传，解除调度锁
    setUploading(true); setUploadProgress(0); setLastUploadError('');
    try {
      const fd = new FormData();
      unique.forEach(f => fd.append('file', f));
      fd.append('path', prefix);
      if (category) fd.append('category', category);
      const res = await api.post('/files/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: evt => { if (evt.total) setUploadProgress(Math.round(evt.loaded * 100 / evt.total)); }
      });
      if (res.data && res.data.success) {
        message.success('上传成功');
        fetchList();
        if (!category && res.data.category) {
          try { const listRes = await api.get('/files/categories'); if (listRes.data?.success) setCategories(listRes.data.categories || []); } catch(e){}
        }
      } else {
        const errMsg = res.data?.error || '上传失败';
        setLastUploadError(errMsg);
        message.error(errMsg);
      }
    } catch (e) {
      console.error(e);
      setLastUploadError('上传失败');
      message.error('上传失败');
    } finally {
      setUploading(false); setUploadProgress(0); setUploadFiles([]); setPendingUploadIds(new Set());
    }
  };
  const uploadProps = {
    multiple: true,
    beforeUpload: file => {
      if (!prefix) { message.error('请先进入一个文件夹'); return Upload.LIST_IGNORE; }
      if (uploading) return Upload.LIST_IGNORE; // 正在上传时忽略新选择
      setUploadFiles(prev => {
        const next = [...prev, file];
        // 若尚未调度上传，开启一次微任务调度
        if (!schedulingRef.current) {
          schedulingRef.current = true;
          Promise.resolve().then(() => {
            setUploadFiles(cur => { triggerUpload(cur); return cur; });
          });
        }
        return next;
      });
      return false; // 阻止默认上传，由我们接管
    },
    fileList: uploadFiles.map(f => ({ uid: f.uid || f.name, name: f.name, status: uploading ? 'uploading' : 'done' })),
    showUploadList: true,
    disabled: uploading
  };

  // 文件预览 & 删除
  const doPreview = async (record) => {
    const baseQuery = `/files/preview?objectName=${encodeURIComponent(record.objectName)}`;
    const isOffice = /\.(docx?|xlsx?|pptx?)$/i.test(record.fileName || '');
    const attempt = async (force) => api.get(force ? baseQuery + '&forceConvert=1' : baseQuery);
    try {
      let res = await attempt(isOffice); // office 直接强制转换
      if (!(res.data && res.data.success)) {
        message.error(res.data?.error || '获取预览链接失败');
        return;
      }
      if (isOffice && !res.data.converted) {
        if (res.data.warning === 'convert_unavailable') {
          message.warning('服务器未安装 LibreOffice，无法转换为 PDF，已返回原文件。');
        } else if (res.data.warning) {
          message.warning('文档转换失败，已返回原文件。');
        }
      } else if (isOffice && res.data.converted) {
        message.success('文档已转换为 PDF');
      }
      const ext = (record.fileName || '').split('.').pop()?.toLowerCase() || '';
      const extMimeMap = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        bmp: 'image/bmp',
        webp: 'image/webp',
        heic: 'image/heic',
        heif: 'image/heif'
      };
      const inferredMime = extMimeMap[ext] || record.mimeType || '';
      const resolvedObjectName = res.data.objectName || record.objectName;
      let previewMime = res.data.mimeType || (res.data.converted ? 'application/pdf' : inferredMime);
      let revokeUrl = null;

      const proxyUrl = buildMinioProxyUrl(resolvedObjectName, { filename: record.fileName });
      const isHeic = ext === 'heic' || ext === 'heif' || (previewMime && previewMime.includes('heic'));
      if (isHeic) {
        try {
          const blob = await fetchBlob(proxyUrl);
          const convertedBlob = await heic2any({ blob, toType: 'image/png' });
          const finalBlob = convertedBlob instanceof Blob ? convertedBlob : new Blob([convertedBlob], { type: 'image/png' });
          revokeUrl = URL.createObjectURL(finalBlob);
          const previewUrl = revokeUrl;
          previewMime = finalBlob.type || 'image/png';

          setPreview(prev => {
            if (prev.revokeUrl) URL.revokeObjectURL(prev.revokeUrl);
            return {
              open: true,
              url: previewUrl,
              mime: previewMime,
              name: record.fileName,
              objectName: resolvedObjectName,
              converted: !!res.data.converted,
              revokeUrl
            };
          });
          return;
        } catch (convertErr) {
          console.error('HEIC 预览转换失败:', convertErr);
          message.warning('当前浏览器不支持 HEIC 图片预览，已为您打开原文件。');
          openWithAuth(proxyUrl);
          return;
        }
      }

      // 其它类型：直接拉取 blob URL（避免新窗口/iframe 无法携带 Authorization）
      const blob = await fetchBlob(proxyUrl);
      revokeUrl = URL.createObjectURL(blob);
      const previewUrl = revokeUrl;

      setPreview(prev => {
        if (prev.revokeUrl) {
          URL.revokeObjectURL(prev.revokeUrl);
        }
        return {
          open: true,
          url: previewUrl,
          mime: previewMime,
          name: record.fileName,
          objectName: resolvedObjectName,
          converted: !!res.data.converted,
          revokeUrl
        };
      });
    } catch (e) {
      console.error(e);
      message.error('获取预览失败');
    }
  };
  const doDelete = async (record) => {
    Modal.confirm({ title: '确认删除文件？', onOk: async () => {
      try {
        const res = await api.delete(`/files?objectName=${encodeURIComponent(record.objectName)}`);
        if (res.data && res.data.success) { message.success('删除成功'); fetchList(); }
        else message.error(res.data?.error || '删除失败');
      } catch(e){ message.error('删除失败'); }
    }});
  };

  // 重命名根目录文件夹
  const submitRename = async () => {
    if (!renameValue.trim()) { message.error('请输入新名称'); return; }
    try {
      const res = await api.post('/files/rename-folder', { oldName: targetFolder, newName: renameValue.trim() });
      if (res.data && res.data.success) {
        message.success('重命名成功');
        setRenameModalOpen(false);
        // 如果当前正在查看被重命名的文件夹，更新 prefix
        if (prefix === targetFolder) setPrefix(renameValue.trim());
        fetchList(true);
      } else message.error(res.data?.error || '重命名失败');
    } catch(e){ message.error('重命名接口失败(后端需实现)'); setRenameModalOpen(false); }
  };

  const submitDeleteFolder = async () => {
    try {
      const res = await api.post('/files/delete-folder', { folder: targetFolder });
      if (res.data && res.data.success) {
        message.success('删除成功');
        setDeleteModalOpen(false);
        if (prefix === targetFolder || prefix.startsWith(targetFolder + '/')) setPrefix('');
        fetchList(true);
      } else message.error(res.data?.error || '删除失败');
    } catch(e){ message.error('删除接口失败(后端需实现)'); setDeleteModalOpen(false); }
  };

  const currentEntries = useMemo(() => {
    const folders = (subFolders || []).map((name) => {
      const fullPath = prefix ? `${prefix}/${name}` : name;
      return {
        key: `folder:${fullPath}`,
        entryType: 'folder',
        fileName: name,
        fullPath,
        objectName: `${fullPath}/`
      };
    });
    const files = (records || []).map((r) => ({
      ...r,
      key: `file:${r.objectName}`,
      entryType: 'file'
    }));
    return [...folders, ...files];
  }, [subFolders, records, prefix]);

  const columns = [
    { title: '序号', render: (_, __, idx) => idx + 1, width: 60 },
    {
      title: '名称',
      dataIndex: 'fileName',
      sorter: (a, b) => String(a.fileName || '').localeCompare(String(b.fileName || '')),
      render: (v, r) => {
        if (r.entryType === 'folder') {
          return (
            <Space>
              <FolderOpenOutlined />
              <a href='#open-folder' onClick={(e) => { e.preventDefault(); setPrefix(r.fullPath); }}>{v}</a>
            </Space>
          );
        }
        const url = buildMinioProxyUrl(r.objectName, { filename: r.fileName }) || r.download_url || r.url || '';
        const isImg = (r.mimeType || '').startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/i.test(r.fileName);
        const isHeicFile = /\.(heic|heif)$/i.test(r.fileName) || ((r.mimeType || '').toLowerCase().includes('heic'));
        return <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <FileOutlined />
          {isImg && url && !isHeicFile && <AuthedImage src={url} alt={r.fileName} style={{ width:36, height:36, objectFit:'cover', borderRadius:4 }} />}
          <a href={url || '#'} onClick={e => { e.preventDefault(); doPreview(r); }}>{v}</a>
        </div>;
      }
    },
    { title: '类型', render: (_, r) => r.entryType === 'folder' ? '文件夹' : (r.mimeType || '文件'), width: 180, sorter: (a, b) => String((a.entryType === 'folder' ? '文件夹' : (a.mimeType || '文件'))).localeCompare(String((b.entryType === 'folder' ? '文件夹' : (b.mimeType || '文件')))) },
    { title: '分类', dataIndex: 'category', render: (v, r) => r.entryType === 'folder' ? '--' : (v || <span style={{ color:'#999' }}>--</span>), width:120 },
    { title: '修改时间', dataIndex: 'lastModified', render: (v, r) => r.entryType === 'folder' ? '--' : (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '--'), width:170, sorter: (a, b) => new Date(a.lastModified || 0).getTime() - new Date(b.lastModified || 0).getTime() },
    { title: '上传人', dataIndex: 'uploader', render: (v, r) => r.entryType === 'folder' ? '--' : (v || '--'), width:120 },
    {
      title: '操作',
      render: (_, r) => {
        if (r.entryType === 'folder') {
          const isRootFolder = !prefix;
          return <Space>
            <Button size='small' type='link' onClick={() => setPrefix(r.fullPath)}>进入</Button>
            {isRootFolder && <Button size='small' onClick={() => { setTargetFolder(r.fileName); setRenameValue(r.fileName); setRenameModalOpen(true); }}>重命名</Button>}
            {isRootFolder && <Button size='small' danger onClick={() => { setTargetFolder(r.fileName); setDeleteModalOpen(true); }}>删除</Button>}
          </Space>;
        }
        return <Space>
          <Button size='small' onClick={() => doPreview(r)}>预览</Button>
          <Button size='small' danger onClick={() => doDelete(r)}>删除</Button>
        </Space>;
      },
      width: 220
    }
  ];

  // 预览内容
  const doPrint = () => {
    if (!preview.url) return;
    // 对 PDF 和图片直接新窗打印；Office 通过在线预览 iframe 打印体验不佳，提示用户另开窗口
    if (/pdf$/.test(preview.url.split('?')[0].toLowerCase()) || (preview.mime || '').includes('pdf')) {
      const w = window.open(preview.url, '_blank'); if (w) w.print(); return;
    }
    if ((preview.mime || '').startsWith('image/')) {
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(`<img src="${preview.url}" style="max-width:100%" onload="window.print();"/>`);
      }
      return;
    }
    // 其它类型直接打开新窗口让用户手动打印
    window.open(preview.url, '_blank');
  };

  const renderPreviewContent = () => {
    if (!preview.url) return <div>无预览链接</div>;
    const mime = (preview.mime || '').toLowerCase();
    if (mime.includes('pdf') || preview.converted) {
      return <SimplePdfViewer url={preview.url} fileName={preview.name} />;
    }
    if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/i.test(preview.name)) {
      return (
        <div style={{ textAlign: 'center' }}>
          <Image
            src={preview.url}
            alt={preview.name}
            style={{ maxHeight: '70vh', objectFit: 'contain' }}
            preview={{ src: preview.url }}
          />
        </div>
      );
    }
    if (preview.name.match(/\.(docx?|xlsx?|pptx?)$/i)) {
      return <div style={{padding:16}}>
        <p style={{marginBottom:12}}>未转换为 PDF。请点击下方按钮下载原文件在本地查看。</p>
        <Button type='primary' onClick={() => downloadWithAuth(buildMinioProxyUrl(preview.objectName, { download:true, filename: preview.name }) || preview.url, preview.name)}>下载原文件</Button>
      </div>;
    }
    return <a href={preview.url} onClick={(e)=>{ e.preventDefault(); openWithAuth(buildMinioProxyUrl(preview.objectName, { filename: preview.name }) || preview.url); }} rel='noreferrer'>在新窗口打开 {preview.name}</a>;
  };

  const closePreview = useCallback(() => {
    setPreview(prev => {
      if (prev.revokeUrl) {
        URL.revokeObjectURL(prev.revokeUrl);
      }
      return { open: false, url: '', mime: '', name: '', objectName: '', converted: false, revokeUrl: null };
    });
  }, []);

  useEffect(() => {
    return () => {
      if (preview.revokeUrl) {
        URL.revokeObjectURL(preview.revokeUrl);
      }
    };
  }, [preview.revokeUrl]);

  return (
    <div>
      <Card title='文件管理' extra={<Space>
        <Button icon={<FolderAddOutlined />} onClick={openCreateFolder}>{prefix ? '新建子文件夹' : '新建文件夹'}</Button>
        <Space>
          <Upload {...uploadProps}><Button icon={<UploadOutlined />} disabled={!prefix || uploading}>{uploading ? '上传中...' : '选择文件自动上传'}</Button></Upload>
          <Tooltip title='可选：为当前上传批次指定分类，未选择则后端自动推断'>
            <Select
              allowClear
              placeholder='选择或输入分类'
              value={category || undefined}
              style={{ minWidth:160 }}
              onChange={val => setCategory(val || '')}
              showSearch
              options={[...categories.map(c => ({ label:c, value:c }))]}
            />
          </Tooltip>
        </Space>
  {uploading && <span style={{ marginLeft:8 }}>{uploadProgress}%</span>}
  {!uploading && lastUploadError && <span style={{ marginLeft:8, color:'#ff4d4f' }}>失败，可重新选择文件重试</span>}
      </Space>}>
        <div style={{ marginBottom: 12 }}>
          <Breadcrumb
            items={[
              { title: <a onClick={(e) => { e.preventDefault(); setPrefix(''); }} href='#root'>根目录</a> },
              ...prefix.split('/').filter(Boolean).map((seg, idx, arr) => {
                const p = arr.slice(0, idx + 1).join('/');
                return {
                  title: idx === arr.length - 1
                    ? <span>{seg}</span>
                    : <a href='#path' onClick={(e) => { e.preventDefault(); setPrefix(p); }}>{seg}</a>
                };
              })
            ]}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 12 }}>
          <Card size='small' title='文件夹结构' bodyStyle={{ padding: 8, maxHeight: 560, overflow: 'auto' }}>
            <Tree
              showIcon
              treeData={treeData}
              selectedKeys={[prefix]}
              loadData={onLoadData}
              onSelect={(keys) => setPrefix((keys && keys.length ? keys[0] : ''))}
              defaultExpandAll
            />
          </Card>

          <Card size='small' title={prefix ? `当前目录：${prefix}` : '当前目录：根目录'} bodyStyle={{ padding: 0 }}>
            <Table
              rowKey='key'
              dataSource={currentEntries}
              columns={columns}
              loading={loading}
              pagination={{ pageSize: 20 }}
              onRow={(record) => ({
                onDoubleClick: () => {
                  if (record.entryType === 'folder') {
                    setPrefix(record.fullPath);
                  }
                }
              })}
            />
          </Card>
        </div>
      </Card>

      {/* 新建文件夹 */}
      <Modal open={folderModalOpen} title={prefix ? '新建子文件夹':'新建文件夹'} onCancel={() => setFolderModalOpen(false)} onOk={createFolder}>
        <Input value={newFolder} onChange={e => setNewFolder(e.target.value)} placeholder='请输入文件夹名称' />
      </Modal>
      {/* 重命名文件夹 */}
      <Modal open={renameModalOpen} title='重命名文件夹' onCancel={() => setRenameModalOpen(false)} onOk={submitRename}>
        <Input value={renameValue} onChange={e => setRenameValue(e.target.value)} placeholder='新文件夹名称' />
      </Modal>
      {/* 删除文件夹 */}
      <Modal open={deleteModalOpen} title='删除文件夹' onCancel={() => setDeleteModalOpen(false)} onOk={submitDeleteFolder}>
        <div>确定删除文件夹 <b>{targetFolder}</b> 及其所有子内容？</div>
      </Modal>
      {/* 文件预览 */}
      <Modal open={preview.open} title={<div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}><span>{preview.name}</span><Button type='primary' size='small' onClick={doPrint} disabled={!preview.url}>打印</Button></div>} onCancel={closePreview} footer={null} width={900}>
        {renderPreviewContent()}
      </Modal>
    </div>
  );
}

export default FileManagerPage;
